import Box from '@mui/material/Box';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import { useEntityNameValidator } from './EntityNameValidator.logic';
import { EntityNameValidatorUiOptions } from './schema';

export { EntityNameValidatorSchema } from './schema';

/**
 * Entity name validator component
 * @param props - Field extension component props
 * @returns Entity name validator component
 */
export const EntityNameValidator = (
  props: FieldExtensionComponentProps<string, EntityNameValidatorUiOptions>,
) => {
  const {
    entityName,
    getHelperText,
    handleInputChange,
    isEditMode,
    isInvalid,
    isValid,
    isValidating,
    maxLength,
    pattern,
    placeholder,
    required,
    title,
  } = useEntityNameValidator(props);

  /**
   * Renders the adornment for the current validation state.
   * @returns Input adornment or undefined when no state indicator is needed
   */
  function renderEndAdornment() {
    if (isValidating) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }

    if (isValid && entityName.trim().length > 0) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon color="success" />
        </InputAdornment>
      );
    }

    if (isInvalid) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon color="error" />
        </InputAdornment>
      );
    }

    return undefined;
  }

  return (
    <Box>
      <TextField
        fullWidth
        label={title}
        placeholder={placeholder}
        required={required}
        value={entityName}
        onChange={handleInputChange}
        disabled={isEditMode}
        variant="standard"
        error={isInvalid}
        color={isValid ? 'success' : undefined}
        focused={isValid}
        helperText={getHelperText()}
        InputProps={{
          endAdornment: renderEndAdornment(),
        }}
        inputProps={{
          pattern: pattern ?? '^[a-zA-Z0-9 ]+$',
          title: 'Only letters, numbers, and spaces allowed.',
          maxLength: maxLength,
        }}
      />
    </Box>
  );
};
